import { type ReactNode } from "react";
import { Flex, Label, LoadingIndicator, Text } from "@jsq/ds/core";
import { FormDatePicker, FormSelect, FormTextInput, useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { TaxIdType } from "~/graphql/supergraphEnums";
import { AIFieldLabel } from "~/pages/Compliance/Common/AIFieldLabel";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { useAIPopulatedFields } from "~/pages/Compliance/Common/hooks/useAIPopulatedFields";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import { INDIVIDUAL_TYPE_OPTIONS } from "~/pages/Compliance/utils/accountTypeUtils";
import { FormValues } from "../../OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm/types";
import { useCountryOptions } from "../AddressFields/CountryFormField/useCountryOptions";
import { AutoProfileCreationCard } from "../AutoProfileCreationCard";
import {
  accountTypeSelectWrap,
  fieldSpacing,
  FormTextInputNameStyle,
  sourceOfWealthLabelRow,
} from "./style_vanilla.css";

const SOURCE_OF_WEALTH_HELP =
  "Describe how this individual accumulated their wealth (e.g., employment income, inheritance, investments).";

interface Props {
  includeTitle?: boolean;
  isEditMode?: boolean;
  inAiExtractionMode?: boolean;
  aiExtractionSupported?: boolean;
  isChildOfJointAccount?: boolean;
  taxIdWarning?: ReactNode;
  showOnboardingFields?: boolean;
}

const CountryIdSelect = ({
  name,
  label,
  placeholder = "Select a country",
}: {
  name: string;
  label: string;
  placeholder?: string;
}) => {
  const { loading, countryOptions } = useCountryOptions();

  if (loading) return <LoadingIndicator />;

  return (
    <FormSelect name={name} label={label} placeholder={placeholder} full data-testid={name}>
      <FormSelect.Section>
        {countryOptions.map((country) => (
          <FormSelect.Option key={country.id} value={String(country.id)}>
            {country.name}
          </FormSelect.Option>
        ))}
      </FormSelect.Section>
    </FormSelect>
  );
};

const IdentificationFields = ({
  typeFieldName,
  numberFieldName,
  taxIdWarning,
  placeholderFromType,
}: {
  typeFieldName: "taxIdType" | "identificationType";
  numberFieldName: "taxId" | "identificationNumber";
  taxIdWarning?: ReactNode;
  placeholderFromType?: string | null;
}) => (
  <>
    <Label>Identification number</Label>
    <Flex justifyContent="flex-start" gap={Sizes.MD} className={fieldSpacing}>
      <FormSelect data-testid="tax-id-type" name={typeFieldName} placeholder="Select type">
        <FormSelect.Section>
          {[TaxIdType.Ssn, TaxIdType.Itin].map((taxIdType) => (
            <FormSelect.Option key={taxIdType} value={taxIdType}>
              {taxIdType.toUpperCase()}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
      <div>
        <FormTextInput
          name={numberFieldName}
          data-testid="tax-id"
          placeholder={placeholderFromType?.toUpperCase() ?? "Identification number"}
        />
        {taxIdWarning}
      </div>
    </Flex>
  </>
);

const IndividualFormFields = ({
  isEditMode = false,
  includeTitle,
  inAiExtractionMode = false,
  aiExtractionSupported = false,
  isChildOfJointAccount = false,
  taxIdWarning,
  showOnboardingFields = false,
}: Props) => {
  const { values } = useFormContext<FormValues>();
  const guidedProfileCreation = useGuidedProfileCreation();
  const showIndividualType =
    !isChildOfJointAccount && (guidedProfileCreation || showOnboardingFields);

  const { hasFieldPopulatedByAI } = useAIPopulatedFields<FormValues>({
    editMode: isEditMode,
    fieldNames: ["firstName", "middleName", "lastName", "dateOfBirth"],
    aiExtractionSupported,
  });
  const showIconForName =
    hasFieldPopulatedByAI("firstName") ||
    hasFieldPopulatedByAI("middleName") ||
    hasFieldPopulatedByAI("lastName");

  const titleField =
    includeTitle && values.isController ? (
      <div className={fieldSpacing}>
        <FormTextInput
          label="Title"
          isFullWidth
          name="title"
          placeholder="e.g., CEO, CFO, COO, Managing Member, General Partner, President, Vice President, Treasurer"
        />
      </div>
    ) : null;

  const nameFields = (
    <>
      <AIFieldLabel label="Name" showIcon={showIconForName} />
      <Flex gap={Sizes.MD} className={fieldSpacing}>
        <FormTextInput
          name="firstName"
          placeholder="First"
          className={FormTextInputNameStyle}
        />
        <FormTextInput
          name="middleName"
          placeholder="Middle"
          className={FormTextInputNameStyle}
        />
        <FormTextInput name="lastName" placeholder="Last" className={FormTextInputNameStyle} />
      </Flex>
    </>
  );

  const individualTypeField = showIndividualType ? (
    <Flex flexDirection="column" className={accountTypeSelectWrap}>
      <FormSelect
        name="accountType"
        label="Individual type"
        placeholder="Select individual type"
        full
      >
        <FormSelect.Section>
          {INDIVIDUAL_TYPE_OPTIONS.map((opt) => (
            <FormSelect.Option key={opt.value} value={opt.value}>
              {opt.label}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
    </Flex>
  ) : null;

  const dateOfBirthField = (
    <div className={fieldSpacing} data-testid="date-of-birth">
      <AIFieldLabel label="Date of birth" showIcon={hasFieldPopulatedByAI("dateOfBirth")} />
      <FormDatePicker maxDate={new Date()} name="dateOfBirth" />
    </div>
  );

  if (showOnboardingFields) {
    return (
      <>
        {titleField}
        {nameFields}
        {individualTypeField}
        {dateOfBirthField}
        <Flex gap={Sizes.MD} className={fieldSpacing}>
          <Flex flexGrow={1} flexShrink={1} flexBasis={0}>
            <CountryIdSelect
              name="placeOfBirthCountryId"
              label="Place of birth"
              placeholder="Select a country"
            />
          </Flex>
          <Flex flexGrow={1} flexShrink={1} flexBasis={0}>
            <CountryIdSelect
              name="nationalityCountryId"
              label="Nationality"
              placeholder="Select a country"
            />
          </Flex>
        </Flex>
        <IdentificationFields
          typeFieldName="identificationType"
          numberFieldName="identificationNumber"
          taxIdWarning={taxIdWarning}
          placeholderFromType={values.identificationType}
        />
        <Flex flexDirection="column" className={fieldSpacing}>
          <Flex alignItems="center" gap={Sizes.XS} className={sourceOfWealthLabelRow}>
            <Label>Source of wealth</Label>
            <ExtendedInfoTooltip message={SOURCE_OF_WEALTH_HELP}>
              <Icon type="question-circle" variant="outline" color={Colors.Neutral600} />
            </ExtendedInfoTooltip>
          </Flex>
          <FormTextInput name="sourceOfWealth" isFullWidth data-testid="source-of-wealth" />
        </Flex>
      </>
    );
  }

  return (
    <>
      {individualTypeField}
      <Text variant="h4" marginBottom="md">
        Biographical Information
      </Text>
      {!isEditMode && aiExtractionSupported && inAiExtractionMode && (
        <AutoProfileCreationCard />
      )}
      {titleField}
      {nameFields}
      {dateOfBirthField}
      <IdentificationFields
        typeFieldName="taxIdType"
        numberFieldName="taxId"
        taxIdWarning={taxIdWarning}
        placeholderFromType={values.taxIdType}
      />
    </>
  );
};

export default IndividualFormFields;
